import React, { useCallback, useState } from 'react'
import {
  TimePicker as MuiTimePicker,
  TimePickerProps,
} from '@mui/x-date-pickers'
import { InputAdornment } from '@common/material/InputAdornment'
import { Clear } from '@mui/icons-material'
import { IconButton } from '@common/material/IconButton'
import { TextInput } from './TextInput'
import moment from 'moment'

type TimePicker<TDate> = TimePickerProps<TDate> &
  React.RefAttributes<HTMLDivElement> & {
    required?: boolean
    error?: boolean
    withClearButton?: boolean
    UTC?: boolean
    dataTest?: string
    name?: string
    onChange?: (date: Date | moment.Moment | null) => void
  }

export const TimePicker: <TDate>(
  props: TimePicker<TDate>,
) => React.ReactElement = ({
  slotProps,
  slots,
  error,
  required,
  withClearButton,
  UTC,
  dataTest,
  disabled,
  ...rest
}) => {
  const [open, setOpen] = useState(false)
  const handleClear = useCallback(() => {
    rest.onChange(null)
  }, [])

  if (Constants.E2E) {
    const { name, label, value, sx } = rest

    return (
      <TextInput
        {...{ 'data-test': dataTest, name, sx }}
        label={name || label}
        inputProps={{ 'data-test': dataTest + '-input' }}
        value={value ? moment(value).format('HH:mm') : ''}
        onChange={(e) => {
          const date = Utils.safeParseEventValue(e)

          if (!isNaN(new Date(date).valueOf())) {
            rest.onChange(new Date(date))
          }
        }}
      />
    )
  }

  return (
    <MuiTimePicker
      format='HH:mm'
      ampm={false}
      disabled={disabled}
      timezone={UTC ? 'UTC' : undefined}
      open={open}
      onClose={() => setOpen(false)}
      slotProps={{
        ...slotProps,
        field: {
          readOnly: true,
          ...slotProps?.field,
        },
        textField: {
          onClick: () => setOpen(true),
          required: required,
          error: error,
          ...slotProps?.textField,
        },
        actionBar: {
          actions: ['clear'],
          ...slotProps?.actionBar,
        },
      }}
      slots={{
        inputAdornment:
          withClearButton && !disabled
            ? () => (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClear()
                    }}
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            : () => null,
        ...slots,
      }}
      {...rest}
    />
  )
}
